import { Component, computed, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ToolchainJobService } from '../../services/api-services/toolchains/toolchain-job.service';
import { CapabilityStore } from '../../services/capability.store';
import { isFinishedJobStatus } from '../../utils/job-run-presentation';
import { routeId } from '../../utils/route-param.util';
import {
  JobPageHeaderComponent,
  JobPageHeaderState,
} from './job-page-header/job-page-header.component';
import { ToolchainTimelineComponent } from './job-timeline/toolchain-timeline.component';

@Component({
  selector: 'app-toolchain-job-page',
  templateUrl: './toolchain-job-page.component.html',
  styleUrls: ['./job-page.component.scss', './toolchain-job-page.component.scss'],
  imports: [JobPageHeaderComponent, ToolchainTimelineComponent],
})
export class ToolchainJobPageComponent {
  /** Services. */
  private readonly route = inject(ActivatedRoute);
  private readonly toolchainJobService = inject(ToolchainJobService);
  private readonly capabilityStore = inject(CapabilityStore);

  /** State. */
  private readonly toolchainId = routeId(this.route.snapshot.paramMap, 'toolchainId') ?? 0;
  readonly jobId = this.route.snapshot.paramMap.get('jobId');
  readonly numericJobId = routeId(this.route.snapshot.paramMap, 'jobId');
  readonly toolchainJob = this.toolchainJobService.getJobById(this.toolchainId, this.numericJobId);
  readonly isJobLoading = this.toolchainJob.isLoading;
  readonly jobError = this.toolchainJob.error;
  readonly isStartingJob = signal(false);

  /** Returns the selected toolchain. */
  readonly toolchain = computed(() => this.capabilityStore.toolchain(this.toolchainId));

  /** Checks whether the toolchain job is loading for the first time. */
  readonly isInitialLoading = computed(() => this.isJobLoading() && !this.toolchainJob.value());

  /** Checks whether the next toolchain step needs user input. */
  readonly isWaitingForUserInput = computed(() => {
    const job = this.toolchainJob.value();
    if (!job) return false;

    const activeJobIndex = job.toolJobs.findIndex(toolJob => !isFinishedJobStatus(toolJob.status));
    const activeStepIndex = activeJobIndex === -1 ? job.toolJobs.length : activeJobIndex;
    const activeStep = this.toolchain()?.steps[activeStepIndex];
    const activeJob = job.toolJobs[activeStepIndex];
    return (
      job.status === 'Action required' || (!!activeStep && !activeJob && !activeStep.automaticStart)
    );
  });

  /** Returns the data consumed by the job page header. */
  readonly headerState = computed<JobPageHeaderState>(() => ({
    isTool: false,
    jobId: this.jobId,
    resource: this.capabilityStore.toolchain(this.toolchainId),
    run: this.toolchainJob.value(),
    loading: this.isJobLoading(),
    failed: !!this.jobError(),
    waitingForInput: this.isWaitingForUserInput(),
    starting: this.isStartingJob(),
    reload: () => this.toolchainJob.reload(),
  }));
}
